import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { Form, Wrapper } from 'components';
import useQuery from 'hooks/useQuery';
import { useAuthStore } from 'stores/authStore';
import { useCreateBrand, useUpdateBrand } from '../../api/brand/hooks';
import { ErrorFeedback } from '../../components/Form/styles';

// eslint-disable-next-line no-unused-vars
const BrandInformationForm = ({ type = 'create', buttonText, initialValues, onNext }) => {
  const query = useQuery();
  const { t } = useTranslation(['pages']);
  const checkoutConfiguration = useAuthStore(
    (state) => state.user?.admin_details?.checkout_configuration,
  );

  const { mutate: createBrand, isPending: isPendingCreate } = useCreateBrand();
  const { mutate: updateBrand, isPending: isPendingUpdate } = useUpdateBrand();

  return (
    <Form
      initialValues={{
        uid: query.get('uid') || initialValues?.uid || '',
        name: query.get('name') || initialValues?.name || '',
        brand_tracking_id: query.get('brand_tracking_id') || initialValues?.brand_tracking_id || '',
      }}
      onSubmit={({ uid, name, brand_tracking_id }) => {
        if (type === 'create') {
          createBrand(
            { name, brand_tracking_id },
            {
              onSuccess: () => {
                if (onNext) onNext();
              },
            },
          );
        } else if (type === 'update') {
          updateBrand(
            {
              uid,
              name,
              brand_tracking_id,
              is_onboarding_process_finished: true,
            },
            {
              onSuccess: () => {
                if (onNext) onNext();
              },
            },
          );
        }
      }}
      validationSchema={Yup.object({
        name: Yup.string()
          .min(3, t('validation:atLeastChars', { number: 3 }))
          .max(50, t('validation:atMostChars', { number: 50 }))
          .matches(/^[a-zA-Z0-9_ ]*$/, t('validation:mustNotContainSpecialChars'))
          .required(t('validation:required')),
      })}
      submitLabel={buttonText || `${t('brandRegistration.save')}`}
      pristine
      renderDefaultButtons={false}
    >
      {({ errors }) => (
        <>
          <Form.Group>
            <Form.Label tooltipText={t('brandRegistration.nameTooltip')}>
              {t('brandRegistration.name')}
            </Form.Label>
            <Form.Input name="name" type="text" placeholder="Hausmart" />
          </Form.Group>
          <Form.Group>
            <Form.Label tooltipText={t('brandRegistration.brandTrackingIdTooltip')}>
              {t('brandRegistration.brandTrackingId', {
                name: checkoutConfiguration?.brand_tracking_id_name
                  ? `(${checkoutConfiguration.brand_tracking_id_name})`
                  : '',
              })}
            </Form.Label>
            <Form.Input name="brand_tracking_id" type="text" />
          </Form.Group>
          <Form.Group>
            <ErrorFeedback>{errors.domain}</ErrorFeedback>
          </Form.Group>
          <Wrapper style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Form.Submit
              size="lg"
              variant="primary"
              ignoreClickedState
              isLoading={isPendingCreate || isPendingUpdate}
              pristine
            >
              {buttonText || `${t('brandRegistration.save')}`}
            </Form.Submit>
          </Wrapper>
        </>
      )}
    </Form>
  );
};

BrandInformationForm.propTypes = {
  type: PropTypes.oneOf(['create', 'update']),
  buttonText: PropTypes.string,
  initialValues: PropTypes.object,
  onNext: PropTypes.func,
};

export default BrandInformationForm;
